import {
  EndType,
  inflatePathsD,
  JoinType,
  type PathD,
  type PathsD,
} from '@countertype/clipper2-ts';
import paper from 'paper/dist/paper-core';

import type { BackerConfig, TextLine, VectorGeometryResult, WeldScoreConfig } from '../types';
import { generateAutoBridges } from './auto-bridge';
import { layoutTextLines } from './glyph-to-path';

export interface ComputeGeometryOptions {
  lines: TextLine[];
  fontMap: Map<string, any>;
  lineSpacingMultiplier?: number;
  overallAlignment?: 'left' | 'center' | 'right';
  weldScoreConfig: WeldScoreConfig;
  backerConfig: BackerConfig;
}

function getPathLength(item: paper.PathItem | null): number {
  if (!item) return 0;
  if (item instanceof paper.Path) {
    return item.length || 0;
  }
  if ((item as paper.CompoundPath).children) {
    let total = 0;
    for (const child of (item as paper.CompoundPath).children) {
      if (child instanceof paper.Path) {
        total += child.length || 0;
      }
    }
    return total;
  }
  return 0;
}

/**
 * Executes the complete Cuttle-style Weld & Score vector geometry pipeline.
 */
export function computeVectorGeometry(options: ComputeGeometryOptions): VectorGeometryResult {
  const {
    lines,
    fontMap,
    lineSpacingMultiplier = 1.15,
    overallAlignment = 'center',
    weldScoreConfig,
    backerConfig,
  } = options;

  // 1. Layout Glyphs with precise physical coordinates (mm)
  const { allGlyphs, bounds } = layoutTextLines(
    lines,
    fontMap,
    lineSpacingMultiplier,
    overallAlignment,
  );

  if (allGlyphs.length === 0) {
    return createEmptyResult();
  }

  // 2. Generate Auto-Bridges for floating dots if enabled
  let bridgeSvgPaths: string[] = [];
  if (weldScoreConfig.connectDots) {
    const bridges = generateAutoBridges(
      allGlyphs,
      weldScoreConfig.bridgeWidthMm,
      weldScoreConfig.bridgeOverhangMm,
    );
    bridgeSvgPaths = bridges.map((b) => b.bridgePathData);
  }

  // Initialize a fresh isolated Paper.js scope
  const scope = new paper.PaperScope();
  scope.setup(new scope.Size(3000, 3000));

  const glyphItems: paper.PathItem[] = [];

  for (const g of allGlyphs) {
    try {
      const item = new scope.CompoundPath(g.pathData);
      if (item && item.bounds.width > 0 && item.bounds.height > 0) {
        glyphItems.push(item);
      }
    } catch {
      // Ignore invalid glyph
    }
  }

  for (const bridgeD of bridgeSvgPaths) {
    try {
      const item = new scope.CompoundPath(bridgeD);
      if (item && item.bounds.width > 0) {
        glyphItems.push(item);
      }
    } catch {
      // Ignore invalid bridge
    }
  }

  if (glyphItems.length === 0) {
    return createEmptyResult();
  }

  let cutPaths: string[] = [];
  let scorePaths: string[] = [];
  const backerPaths: string[] = [];
  const guidePaths: string[] = [];

  let cutPerimeterMm = 0;
  let scorePerimeterMm = 0;

  // 3. Compute Weld (Cut Outline)
  let unitedOutline: paper.PathItem | null = null;

  if (weldScoreConfig.weldEnabled) {
    try {
      // Successive Boolean Union of all glyphs and bridges
      unitedOutline = glyphItems[0].clone() as paper.PathItem;
      for (let i = 1; i < glyphItems.length; i++) {
        const next = glyphItems[i];
        const merged = unitedOutline.unite(next, { insert: false }) as paper.PathItem;
        if (merged && merged.bounds.width > 0) {
          unitedOutline.remove();
          unitedOutline = merged;
        }
      }

      if (unitedOutline && unitedOutline.pathData) {
        cutPaths.push(unitedOutline.pathData);
        cutPerimeterMm += getPathLength(unitedOutline);
      }
    } catch (err) {
      console.warn('[WeldAndScore] Paper.js unite fallback:', err);
      cutPaths = allGlyphs.map((g) => g.pathData);
    }
  } else {
    // No weld: Cut each glyph independently
    cutPaths = allGlyphs.map((g) => g.pathData);
    for (const item of glyphItems) {
      cutPerimeterMm += getPathLength(item);
    }
  }

  // 4. Compute Score Lines (Internal Overlaps for layered look)
  if (weldScoreConfig.weldEnabled && weldScoreConfig.scoreEnabled && glyphItems.length > 1) {
    const rawScorePaths: string[] = [];

    // Pairwise overlap intersection between adjacent / intersecting glyphs
    for (let i = 0; i < glyphItems.length; i++) {
      const current = glyphItems[i];

      for (let j = i + 1; j < glyphItems.length; j++) {
        const other = glyphItems[j];
        if (!current.bounds.intersects(other.bounds)) continue;

        try {
          const overlap = current.intersect(other, { insert: false }) as paper.PathItem;
          const overlapLen = getPathLength(overlap);

          if (overlap && overlap.pathData && overlapLen >= weldScoreConfig.minScoreLengthMm) {
            rawScorePaths.push(overlap.pathData);
            scorePerimeterMm += overlapLen;
            overlap.remove();
          }
        } catch {
          // Ignore boolean failure on non-manifold edges
        }
      }
    }

    scorePaths = rawScorePaths;
  }

  // 5. Backer & Mounting Features (Outset Backer, Base Bar, Cake Topper)
  const actualBounds = unitedOutline
    ? {
        minX: unitedOutline.bounds.left,
        maxX: unitedOutline.bounds.right,
        minY: unitedOutline.bounds.top,
        maxY: unitedOutline.bounds.bottom,
        widthMm: unitedOutline.bounds.width,
        heightMm: unitedOutline.bounds.height,
      }
    : bounds;

  if (backerConfig.type === 'outset' && (unitedOutline || glyphItems.length > 0)) {
    const sourceItem = unitedOutline || glyphItems[0];
    const offsetPaths = generateClipperOffset(
      sourceItem,
      backerConfig.offsetMm,
      backerConfig.cornerJoin,
    );
    backerPaths.push(...offsetPaths);

    if (backerConfig.scoreGuidesOnBacker && cutPaths.length > 0) {
      guidePaths.push(...cutPaths);
    }

    if (backerConfig.hangerHoles) {
      const holes = generateHangerHoles(actualBounds, backerConfig);
      backerPaths.push(...holes);
    }
  } else if (backerConfig.type === 'base-bar') {
    const bar = generateBaseBar(actualBounds, backerConfig);
    backerPaths.push(bar);
  } else if (backerConfig.type === 'cake-topper' && unitedOutline) {
    const stake = generateCakeTopperStake(actualBounds, backerConfig);
    try {
      const stakeItem = new scope.CompoundPath(stake);
      const weldedTopper = unitedOutline.unite(stakeItem, { insert: false }) as paper.PathItem;
      if (weldedTopper && weldedTopper.pathData) {
        cutPaths = [weldedTopper.pathData];
      }
    } catch {
      cutPaths.push(stake);
    }
  } else if (backerConfig.type === 'frame-circle' || backerConfig.type === 'frame-oval') {
    const frame = generateFrameShape(actualBounds, backerConfig);
    backerPaths.push(frame);
  }

  // Laser Time Estimation (Assuming 15mm/s cut speed, 100mm/s score speed, plus 5s positioning)
  const estimatedTimeSec = Math.round(cutPerimeterMm / 15 + scorePerimeterMm / 100 + 5);

  return {
    cutPaths,
    scorePaths,
    backerPaths,
    guidePaths,
    rawGlyphCount: allGlyphs.length,
    bounds: {
      minX: round(actualBounds.minX),
      minY: round(actualBounds.minY),
      maxX: round(actualBounds.maxX),
      maxY: round(actualBounds.maxY),
      widthMm: round(actualBounds.widthMm),
      heightMm: round(actualBounds.heightMm),
    },
    stats: {
      cutLengthMm: round(cutPerimeterMm),
      scoreLengthMm: round(scorePerimeterMm),
      totalPerimeterMm: round(cutPerimeterMm + scorePerimeterMm),
      estimatedLaserTimeSec: estimatedTimeSec,
    },
  };
}

/**
 * Uses Clipper2 to calculate smooth polygon outset offsets for backing plates.
 */
function generateClipperOffset(
  paperItem: paper.PathItem,
  offsetMm: number,
  cornerJoin: 'round' | 'miter' | 'bevel' = 'round',
): string[] {
  const pathsD: PathsD = [];

  const extractPath = (p: paper.Path) => {
    if (!p.segments || p.segments.length < 3) return;
    const poly: PathD = [];
    const stepLength = Math.max(0.5, p.length / 150);
    const numSteps = Math.min(300, Math.max(20, Math.floor(p.length / stepLength)));

    for (let s = 0; s <= numSteps; s++) {
      const pt = p.getPointAt((s / numSteps) * p.length);
      if (pt) {
        poly.push({ x: pt.x, y: pt.y });
      }
    }
    if (poly.length > 2) {
      pathsD.push(poly);
    }
  };

  if ((paperItem as paper.CompoundPath).children) {
    for (const child of (paperItem as paper.CompoundPath).children) {
      if (child instanceof paper.Path) {
        extractPath(child);
      }
    }
  } else if (paperItem instanceof paper.Path) {
    extractPath(paperItem);
  }

  if (pathsD.length === 0) return [];

  let joinType = JoinType.Round;
  if (cornerJoin === 'miter') joinType = JoinType.Miter;
  if (cornerJoin === 'bevel') joinType = JoinType.Bevel;

  const inflated = inflatePathsD(pathsD, offsetMm, joinType, EndType.Polygon);
  const resultSvgPaths: string[] = [];

  for (const poly of inflated) {
    if (poly.length < 3) continue;
    let d = `M ${round(poly[0].x)} ${round(poly[0].y)} `;
    for (let i = 1; i < poly.length; i++) {
      d += `L ${round(poly[i].x)} ${round(poly[i].y)} `;
    }
    d += 'Z';
    resultSvgPaths.push(d);
  }

  return resultSvgPaths;
}

/**
 * Generates mounting hanger holes.
 */
function generateHangerHoles(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  config: BackerConfig,
): string[] {
  const r = config.hangerHoleDiameterMm / 2;
  const inset = config.hangerHoleInsetMm;
  const holes: string[] = [];

  if (config.hangerHoleCount === 1) {
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = bounds.minY - inset;
    holes.push(
      `M ${round(cx - r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx + r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx - r)} ${round(cy)} Z`,
    );
  } else {
    const cy = bounds.minY - inset;
    const cx1 = bounds.minX + inset * 2;
    const cx2 = bounds.maxX - inset * 2;
    holes.push(
      `M ${round(cx1 - r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx1 + r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx1 - r)} ${round(cy)} Z`,
    );
    holes.push(
      `M ${round(cx2 - r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx2 + r)} ${round(cy)} A ${r} ${r} 0 1 0 ${round(cx2 - r)} ${round(cy)} Z`,
    );
  }

  return holes;
}

/**
 * Generates solid base bar for freestanding desk signs.
 */
function generateBaseBar(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  config: BackerConfig,
): string {
  const x1 = bounds.minX - config.baseBarExtendMm;
  const x2 = bounds.maxX + config.baseBarExtendMm;
  const y1 = bounds.maxY - 2;
  const y2 = bounds.maxY + config.baseBarHeightMm;
  const radius = 2.0;

  return `M ${round(x1 + radius)} ${round(y1)} 
          L ${round(x2 - radius)} ${round(y1)} 
          Q ${round(x2)} ${round(y1)} ${round(x2)} ${round(y1 + radius)} 
          L ${round(x2)} ${round(y2 - radius)} 
          Q ${round(x2)} ${round(y2)} ${round(x2 - radius)} ${round(y2)} 
          L ${round(x1 + radius)} ${round(y2)} 
          Q ${round(x1)} ${round(y2)} ${round(x1)} ${round(y2 - radius)} 
          L ${round(x1)} ${round(y1 + radius)} 
          Q ${round(x1)} ${round(y1)} ${round(x1 + radius)} ${round(y1)} Z`;
}

/**
 * Generates cake topper insertion stake.
 */
function generateCakeTopperStake(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  config: BackerConfig,
): string {
  const cx = (bounds.minX + bounds.maxX) / 2;
  const halfW = config.cakeTopperProngWidthMm / 2;
  const topY = bounds.maxY - 4;
  const botY = bounds.maxY + config.cakeTopperProngLengthMm;

  return `M ${round(cx - halfW)} ${round(topY)} 
          L ${round(cx + halfW)} ${round(topY)} 
          L ${round(cx + halfW * 0.7)} ${round(botY - 8)} 
          L ${round(cx)} ${round(botY)} 
          L ${round(cx - halfW * 0.7)} ${round(botY - 8)} Z`;
}

/**
 * Generates enclosing oval or circle frame.
 */
function generateFrameShape(
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    widthMm: number;
    heightMm: number;
  },
  config: BackerConfig,
): string {
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  const rx = bounds.widthMm / 2 + config.offsetMm * 2;
  const ry = bounds.heightMm / 2 + config.offsetMm * 2;
  const ringThickness = 3.5;

  return `M ${round(cx - rx)} ${round(cy)} 
          A ${round(rx)} ${round(ry)} 0 1 0 ${round(cx + rx)} ${round(cy)} 
          A ${round(rx)} ${round(ry)} 0 1 0 ${round(cx - rx)} ${round(cy)} Z
          M ${round(cx - (rx - ringThickness))} ${round(cy)} 
          A ${round(rx - ringThickness)} ${round(ry - ringThickness)} 0 1 1 ${round(cx + (rx - ringThickness))} ${round(cy)} 
          A ${round(rx - ringThickness)} ${round(ry - ringThickness)} 0 1 1 ${round(cx - (rx - ringThickness))} ${round(cy)} Z`;
}

function createEmptyResult(): VectorGeometryResult {
  return {
    cutPaths: [],
    scorePaths: [],
    backerPaths: [],
    guidePaths: [],
    rawGlyphCount: 0,
    bounds: { minX: 0, minY: 0, maxX: 100, maxY: 30, widthMm: 100, heightMm: 30 },
    stats: { cutLengthMm: 0, scoreLengthMm: 0, totalPerimeterMm: 0, estimatedLaserTimeSec: 0 },
  };
}

function round(n: number, decimals = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}
